import React, { createContext, FC, useContext, useEffect, useState } from 'react';
import { fetchAndActivate, getRemoteConfig } from 'firebase/remote-config';
import { firebaseApp } from 'firebaseConfig';
import { ToastContainer } from 'react-toastify';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import useLocale, { UseLocale } from 'hooks/useLocale';
import useSearchHook, { UseSearch } from 'hooks/useSearch';
import useTenantHook, { UseTenant } from 'hooks/useTenant';
import { useUser } from 'store/user/useUser';

/* Interface for the app context */
interface AppState {
  search: UseSearch;
  locale: UseLocale;
  tenant: UseTenant;
}

/* Creating the context */
export const AppContext = createContext<AppState | undefined>(undefined);

const AppContextProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [loadingRemoteConfig, setLoadingRemoteConfig] = useState(true);
  const user = useUser();
  const locale = useLocale(user.isAuthenticated ? user.user_id : '');
  const search = useSearchHook();
  const tenant = useTenantHook();
  // set up the axios interceptors using the auth0 access token

  useEffect(() => {
    fetchAndActivate(getRemoteConfig(firebaseApp)).finally(() => {
      setLoadingRemoteConfig(false);
    });
  }, []);

  return (
    <AppContext.Provider
      value={{
        search,
        locale,
        tenant,
      }}
    >
      <ToastContainer />
      {loadingRemoteConfig ? <PageSpinner /> : children}
    </AppContext.Provider>
  );
};

export default AppContextProvider;

/* Searches for a set/device in the DB */
export const useSearch = (): AppState['search'] => {
  const context = useContext(AppContext);

  if (!context?.search) throw new Error('Search state must be consumed within AppProvider!');

  return context.search;
};

/* Searches for a set/device in the DB */
export const useTenant = (): AppState['tenant'] => {
  const context = useContext(AppContext);

  if (!context?.tenant) throw new Error('Search state must be consumed within AppProvider!');

  return context.tenant;
};

export const useLanguage = (): AppState['locale'] => {
  const context = useContext(AppContext);

  if (!context?.search) throw new Error('Locale state must be consumed within AppProvider!');

  return context.locale;
};
